import { DateRange } from "@/constants/filters/filters";

/**
 * Default date range for dashboard list endpoints: last 30 days.
 * End date is tomorrow (local) so items created today are never missed.
 */
export function getLast30Days(): { startDate: string; endDate: string } {
  const end = new Date()
  end.setDate(end.getDate() + 1)
  const start = new Date()
  start.setDate(start.getDate() - 30)
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString()
  }
}

/**
 * Format a Date for use as value of <input type="datetime-local"> (local time).
 * Browsers require YYYY-MM-DDTHH:mm in local time for the input to be editable.
 */
export function toLocalDatetimeLocalString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${h}:${min}`
}

/**
 * Format a Date as YYYY-MM-DD (local date only) for <input type="date">.
 * type="date" is more reliably editable across browsers than datetime-local.
 */
export function toLocalDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Format an ISO date string for display; returns "—" for invalid or missing values.
 * Use for activity timestamps and other dashboard dates to avoid showing "Invalid Date".
 */
export function formatDateTimeSafe(isoString: string | undefined | null): string {
  if (isoString == null || isoString === '') return '—'
  const d = new Date(isoString)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

/**
 * Format an ISO date string as a date-only display (e.g. "Apr 4, 2026").
 * Use for renewal dates, transaction dates, and anywhere time is not needed.
 */
export function formatDateSafe(isoString: string | undefined | null): string {
  if (isoString == null || isoString === '') return '—'
  const d = new Date(isoString)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Get start and end dates for a given date range selection. 
 * End date is always now (in ISO format) and start date is calculated based on the range.
 */
export function getDateRange(range: DateRange): { startDate: string; endDate: string } {
  const now = new Date()
  let daysAgo = 30
  if (range === DateRange.LAST_7_DAYS) daysAgo = 7
  else if (range === DateRange.LAST_1_MONTH) daysAgo = 30
  else if (range === DateRange.LAST_3_MONTHS) daysAgo = 90
  else if (range === DateRange.LAST_1_YEAR) daysAgo = 365
  else if (range === DateRange.LAST_2_YEARS) daysAgo = 730

  const start = new Date(now)
  start.setDate(start.getDate() - daysAgo)
  return {
    startDate: start.toISOString(),
    endDate: now.toISOString()
  }
}
